import React from "react";
import { ShieldCheck, Database, Lock, FileText, Users } from "lucide-react";
import Footer from "../Footer";import { useTranslation } from 'react-i18next';


/* ------------------ SMALL HELPERS ------------------ */

const Highlight = ({ children }) => (
  <span className="font-semibold text-purple-700">{children}</span>
);

const Divider = () => (
  <div className="my-14 h-[1px] w-full bg-gradient-to-r from-transparent via-purple-200 to-transparent" />
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="relative">
    <div className="flex items-start gap-4 mb-6">
      <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>

      <h2
        className="
          text-2xl md:text-3xl font-semibold
          bg-gradient-to-r from-purple-700 via-purple-500 to-indigo-600
          bg-clip-text text-transparent
        "
      >
        {title}
      </h2>
    </div>

    <div className="pl-[3.25rem] text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

/* ------------------ PAGE ------------------ */

const PrivacyPolicy = () => {
  return (
    <>

      <main className="relative bg-white overflow-hidden">
        {/* BACKGROUND */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50 via-white to-white pointer-events-none" />

        <div className="relative container mx-auto px-6 py-24 max-w-8xl mt-6">
          {/* PAGE HERO */}
          <header className="mb-12 text-left">
            <p className="inline-block
    text-sm uppercase tracking-widest font-semibold
    text-purple-700
    px-4 py-1.5 rounded-full
    bg-gradient-to-r
    from-purple-100 via-purple-50 to-indigo-100
    mb-3">
              Legal & Compliance
            </p>

            <h1
              className="
                text-4xl md:text-5xl font-semibold leading-tight bg-gradient-to-r
                    from-pink-600 via-purple-600 to-indigo-600
                    bg-clip-text text-transparent pb-2
              "
            >
              Privacy Policy
            </h1>

            <p className="mt-6 text-gray-600 max-w-4xl text-lg">
              At <Highlight>DigiQin</Highlight>, protecting your privacy and
              safeguarding your business data is a core responsibility. This
              policy explains how we collect, use, and secure information across
              all DigiQin platforms.
            </p>
          </header>

          {/* ABOUT */}
          <Section title="About DigiQin" icon={ShieldCheck} className="pb-2">
            <p>
              DigiQin is a <Highlight>technology-driven SaaS company</Highlight>{" "}
              delivering digital solutions for:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Logistics & Shipment Booking Software</li>
              <li>Inventory Management Systems</li>
              <li>Attendance & Workforce Management</li>
              <li>Website Development & Digital Marketing</li>
              <li>Automation, GPS, WhatsApp & API Integrations</li>
            </ul>
            <p>
              This Privacy Policy applies to <Highlight>all DigiQin products</Highlight>,
              services, websites, and applications.
            </p>
          </Section>

          <Divider />

          {/* INFORMATION COLLECTED */}
          <Section title="Information We Collect" icon={Database}>
            <p className="font-semibold text-gray-800">
              Personal & Account Information
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email address, and mobile number</li>
              <li>Company name and account credentials</li>
              <li>Billing and communication details</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">
              Business & Operational Data
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Shipment, inventory, attendance, and invoice records</li>
              <li>Customer, vendor, and internal business data</li>
            </ul>

            <p className="mt-3">
              <Highlight>Data Ownership:</Highlight> All business data entered
              into DigiQin remains the property of the customer.
            </p>

            <p className="font-semibold text-gray-800 mt-4">
              Technical & Usage Information
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>IP address, browser type, and device details</li>
              <li>Login activity and feature usage logs</li>
            </ul>
          </Section>

          <Divider />

          {/* DATA USAGE */}
          <Section title="How We Use Your Information" icon={FileText}>
            <ul className="list-disc pl-6 space-y-2">
              <li>Operate and maintain DigiQin platforms</li>
              <li>Create and manage user accounts</li>
              <li>Enable logistics, inventory, and workforce workflows</li>
              <li>Send alerts, notifications, and service updates</li>
              <li>Provide customer support and onboarding</li>
              <li>Improve system performance and features</li>
              <li>Process subscriptions and payments</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>
          </Section>

          <Divider />

          {/* SECURITY */}
          <Section title="Data Security" icon={Lock}>
            <p>
              DigiQin implements <Highlight>industry-standard security measures</Highlight>,
              including encrypted data transmission, secure servers, access
              control mechanisms, and regular monitoring.
            </p>
            <p>
              While we strive to protect your data, no digital system can
              guarantee <Highlight>absolute security</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* USER RIGHTS */}
          <Section title="User Rights" icon={Users}>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access and review your personal data</li>
              <li>Request correction or deletion of information</li>
              <li>Opt out of non-essential communications</li>
            </ul>
            <p>
              Requests can be sent to{" "}
              <Highlight>support@digiqin.com</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* CONTACT */}
          <Section title="Contact Information" icon={ShieldCheck}>
            <p>
              📧 Email: <Highlight>support@digiqin.com</Highlight>
            </p>
            <p>
              📞 Phone: <Highlight>+91 98925 75520</Highlight>
            </p>
            <p>
              🌐 Website: <Highlight>https://www.digiqin.com</Highlight>
            </p>
          </Section>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
